/*
 * Customer Auth API client.
 *
 * Müşteri (QR menü kullanıcısı) tarafı için Sanctum bearer auth akışı.
 * Endpoints (auth:customer guard, except where marked PUBLIC):
 *
 *   POST /api/customer/auth/google              [PUBLIC]  → Google ID token ile login
 *   POST /api/customer/auth/phone/request-otp   [PUBLIC]  → +994... numarasına SMS OTP gönder
 *   POST /api/customer/auth/phone/verify-otp    [PUBLIC]  → 6 haneli kodu doğrula, isim opsiyonel
 *   GET  /api/customer/me                       [AUTH]    → mevcut müşteri profili
 *   PATCH/api/customer/me                       [AUTH]    → profil güncelle (name/email/birth_date/preferences)
 *   POST /api/customer/auth/logout              [AUTH]    → mevcut Sanctum token'ı revoke
 *
 * Token saklama: localStorage anahtarı `rezmo-customer-token`. Cached profile
 * `rezmo-customer-profile`. Bu modül sadece HTTP'den sorumlu — UI state'i
 * `CustomerAuthContext` tutar.
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import { base_url } from './index';

// ---------- Storage helpers ----------

export const CUSTOMER_TOKEN_KEY = 'rezmo-customer-token';
export const CUSTOMER_PROFILE_KEY = 'rezmo-customer-profile';

export const readCustomerToken = (): string | null => {
  try {
    return localStorage.getItem(CUSTOMER_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const writeCustomerToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
    else localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    /* private mode / quota — ignore */
  }
};

export const readCachedCustomer = (): CustomerUser | null => {
  try {
    const raw = localStorage.getItem(CUSTOMER_PROFILE_KEY);
    return raw ? (JSON.parse(raw) as CustomerUser) : null;
  } catch {
    return null;
  }
};

export const writeCachedCustomer = (user: CustomerUser | null): void => {
  try {
    if (user) localStorage.setItem(CUSTOMER_PROFILE_KEY, JSON.stringify(user));
    else localStorage.removeItem(CUSTOMER_PROFILE_KEY);
  } catch {
    /* ignore */
  }
};

// ---------- Domain types ----------

export interface CustomerUser {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  avatar: string | null;
  birth_date?: string | null;
  preferences?: Record<string, any> | null;
  // Backend may include verification flags / google_id; we keep them open.
  [extra: string]: any;
}

export interface CustomerAuthResponse {
  token: string;
  customer: CustomerUser;
  is_new?: boolean;
}

export interface CustomerOtpRequestResponse {
  cooldown_seconds: number;
  expires_in_seconds: number;
}

export interface CustomerProfileUpdatePayload {
  name?: string;
  email?: string | null;
  birth_date?: string | null;
  preferences?: Record<string, any> | null;
}

// ---------- HTTP client (shared by Auth + Favorites + Ratings) ----------

/** Axios instance configured with the customer Sanctum bearer header. The
 *  `Authorization` header is attached lazily per-request through an
 *  interceptor so token rotations don't require rebuilding the client. */
export const customerClient: AxiosInstance = axios.create({
  baseURL: base_url,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 15000,
});

customerClient.interceptors.request.use((config) => {
  const token = readCustomerToken();
  if (token) {
    // Axios v1 expects AxiosHeaders, ama runtime'da plain object'i de kabul ediyor.
    // TS sıkı tip kontrolünü atlatmak için `as any` üzerinden ata.
    const headers = (config.headers as any) || {};
    headers.Authorization = `Bearer ${token}`;
    config.headers = headers;
  }
  return config;
});

/** Centralized 401 handler — when the customer's Sanctum token is rejected
 *  by the backend we wipe local state so the UI naturally falls back to the
 *  signed-out shell on the next render. The throw is preserved so callers
 *  still see the error and can surface a friendlier message via the
 *  Dynamic Island bildirim sistemi. */
customerClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error?.response?.status === 401) {
      writeCustomerToken(null);
      writeCachedCustomer(null);
    }
    return Promise.reject(error);
  },
);

// ---------- Endpoints ----------

/** Google ID token ile giriş. SDK'dan alınan `credential`'ı backend'e ilet. */
export async function googleSignIn(idToken: string): Promise<CustomerAuthResponse> {
  const res = await customerClient.post<CustomerAuthResponse>(
    '/customer/auth/google',
    { id_token: idToken },
  );
  return res.data;
}

/** Telefon OTP iste. Numara E.164 formatında olmalı (örn: +994501234567). */
export async function requestPhoneOtp(
  phone: string,
): Promise<CustomerOtpRequestResponse> {
  const res = await customerClient.post<CustomerOtpRequestResponse>(
    '/customer/auth/phone/request-otp',
    { phone },
  );
  return res.data;
}

/** Telefon OTP doğrula. `name` opsiyonel — yeni kullanıcılar için kayıt sırasında yollanır. */
export async function verifyPhoneOtp(payload: {
  phone: string;
  code: string;
  name?: string;
}): Promise<CustomerAuthResponse> {
  const res = await customerClient.post<CustomerAuthResponse>(
    '/customer/auth/phone/verify-otp',
    payload,
  );
  return res.data;
}

/** Mevcut müşteri profili — bearer header otomatik ekleniyor. */
export async function getCustomerMe(): Promise<CustomerUser> {
  const res = await customerClient.get<CustomerUser | { customer: CustomerUser }>(
    '/customer/me',
  );
  // Backend bazen `{ customer: {...} }`, bazen direkt customer objesi dönebilir.
  // Her iki şekli de tolere ediyoruz.
  const data: any = res.data;
  if (data && typeof data === 'object' && 'customer' in data && data.customer) {
    return data.customer as CustomerUser;
  }
  return data as CustomerUser;
}

/** Profil güncelle (name, email, birth_date, preferences). */
export async function updateCustomerMe(
  payload: CustomerProfileUpdatePayload,
): Promise<CustomerUser> {
  const res = await customerClient.patch<CustomerUser | { customer: CustomerUser }>(
    '/customer/me',
    payload,
  );
  const data: any = res.data;
  if (data && typeof data === 'object' && 'customer' in data && data.customer) {
    return data.customer as CustomerUser;
  }
  return data as CustomerUser;
}

/** Çıkış — mevcut Sanctum token'ı revoke et. Network başarısız olsa bile
 *  yerel state'i temizle. */
export async function customerLogout(): Promise<void> {
  try {
    await customerClient.post('/customer/auth/logout');
  } catch {
    /* sessizce yut — local cleanup her halükarda olmalı */
  } finally {
    writeCustomerToken(null);
    writeCachedCustomer(null);
  }
}

// ---------- Phone helpers ----------

/** AZ numarası: +994 prefix sabit. Kullanıcı 9 haneli local numara girer
 *  (50 123 45 67), helper +994 ekler ve sadece rakam tutar. */
export const AZ_PHONE_PREFIX = '+994';

export const sanitizeAzLocalDigits = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, 9);

export const buildAzPhone = (localDigits: string): string =>
  `${AZ_PHONE_PREFIX}${sanitizeAzLocalDigits(localDigits)}`;

/** Sayısal olarak geçerli mi? AZ mobil numarası 9 haneli (ör: 501234567). */
export const isValidAzLocal = (localDigits: string): boolean => {
  const d = sanitizeAzLocalDigits(localDigits);
  return d.length === 9;
};
